import { toast } from 'react-hot-toast';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { Box, ListItemIcon, ListItemText, MenuItem, Popover, Typography } from '@mui/material';
import type { PopoverProps } from '@mui/material';

const languageOptions = {
  en: {
    icon: '/static/icons/us_flag.svg',
    label: 'English',
  },
  de: {
    icon: '/static/icons/de_flag.svg',
    label: 'German',
  },
  es: {
    icon: '/static/icons/es_flag.svg',
    label: 'Spanish',
  },
} as const;

type Language = keyof typeof languageOptions;

interface LanguagePopoverProps extends Omit<PopoverProps, 'open' | 'anchorEl' | 'onClose'> {
  anchorEl: PopoverProps['anchorEl'];
  onClose?: () => void;
  open: boolean;
}

export const LanguagePopover = (props: LanguagePopoverProps) => {
  const { anchorEl, onClose, open, ...other } = props;
  const { i18n, t } = useTranslation();

  const handleChange = (language: Language) => {
    onClose?.();
    i18n.changeLanguage(language);
    toast.success(t('Language changed'));
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      keepMounted
      onClose={onClose}
      open={open}
      PaperProps={{ sx: { width: 240 } }}
      transitionDuration={0}
      {...other}
    >
      {Object.keys(languageOptions).map((language) => (
        <MenuItem onClick={() => handleChange(language as Language)} key={language}>
          <ListItemIcon>
            <Box
              sx={{
                display: 'flex',
                height: 20,
                width: 20,
                '& img': {
                  width: '100%',
                },
              }}
            >
              <Image alt={languageOptions[language as Language].label} height={20} src={languageOptions[language as Language].icon} width={20} />
            </Box>
          </ListItemIcon>
          <ListItemText primary={<Typography variant='subtitle2'>{languageOptions[language as Language].label}</Typography>} />
        </MenuItem>
      ))}
    </Popover>
  );
};
